/**
 * Silent bot protection — no visible captcha, no Google widgets.
 * Signals: honeypot, dwell time, human interaction, and the invisible proof-of-work captcha
 * (lib/invisibleCaptcha.ts), which is the part the server actually verifies.
 */

import { clientError, type AppErrorInfo } from './errorCodes';
import type { InvisibleProof } from './invisibleCaptcha';

export type SilentGuardState = {
  /** Hidden field bots tend to fill */
  honeypot: string;
  /** Epoch ms when the form mounted */
  mountedAt: number;
  /** True after mouse / key / touch / pointer */
  interacted: boolean;
};

export function createSilentGuard(): SilentGuardState {
  return { honeypot: '', mountedAt: Date.now(), interacted: false };
}

export type SilentGuardPayload = {
  honeypot: string;
  dwellMs: number;
  interacted: boolean;
  pow: InvisibleProof;
};

export type SilentGuardResult = { ok: true; payload: SilentGuardPayload } | { ok: false; error: AppErrorInfo };

/** Client-side pre-checks before the invisible captcha proof is attached. */
export function checkHumanSignals(state: SilentGuardState, opts?: { minDwellMs?: number }): AppErrorInfo | null {
  const minDwellMs = opts?.minDwellMs ?? 1500;
  if (Date.now() - state.mountedAt < minDwellMs) return clientError('TOO_FAST');
  if (!state.interacted) return clientError('NO_INTERACTION');
  return null;
}

export function buildSilentPayload(state: SilentGuardState, pow: InvisibleProof): SilentGuardPayload {
  return {
    honeypot: state.honeypot,
    dwellMs: Date.now() - state.mountedAt,
    interacted: state.interacted,
    pow,
  };
}
